import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { matchTeamAvg, noteColor, formatDateDE, spieltypLabel } from "@/lib/matchStats";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, BarChart3, Trash2, ChevronRight, Trophy, ClipboardList } from "lucide-react";
import { toast } from "sonner";

export default function Matches() {
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/matches");
      setMatches(data);
    } catch {
      toast.error("Spiele konnten nicht geladen werden");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/matches/${confirmDelete.id}`);
      toast.success("Spiel gelöscht");
      setConfirmDelete(null);
      load();
    } catch { toast.error("Löschen fehlgeschlagen"); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-brand-lighter mb-1">Match-Bewertung</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-zinc-100">Spieler-Note</h1>
          <p className="text-zinc-400 text-sm mt-1">
            <span className="text-zinc-100 font-semibold" data-testid="matches-count">{matches.length}</span> Spiele erfasst
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => navigate("/noten/saison")} variant="outline"
            className="bg-transparent border-zinc-700 text-zinc-100 hover:text-brand-lighter hover:bg-zinc-900 rounded-md h-11"
            data-testid="match-season-btn">
            <BarChart3 className="h-4 w-4 mr-2" /> Saisonübersicht
          </Button>
          <Button onClick={() => navigate("/noten/neu")}
            className="bg-brand hover:bg-brand-light text-white font-semibold rounded-md h-11"
            data-testid="new-match-btn">
            <Plus className="h-4 w-4 mr-2" /> Neues Spiel
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-zinc-500">Lädt…</div>
      ) : matches.length === 0 ? (
        <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-10 text-center">
          <ClipboardList className="h-10 w-10 text-zinc-700 mx-auto mb-3" />
          <div className="font-display text-xl font-semibold text-zinc-200 mb-1">Noch keine Spiele</div>
          <div className="text-sm text-zinc-500 mb-5">Erfasse dein erstes Spiel und vergib Bewertungen.</div>
          <Button onClick={() => navigate("/noten/neu")} className="bg-brand hover:bg-brand-light text-white font-semibold rounded-md">
            <Plus className="h-4 w-4 mr-2" /> Neues Spiel
          </Button>
        </div>
      ) : (
        <div className="space-y-2" data-testid="matches-list">
          {matches.map((m) => {
            const avg = matchTeamAvg(m);
            const ratedCount = Object.keys(m.ratings || {}).length;
            const hasResult = m.tore_eigen != null && m.tore_gegner != null;
            const won = hasResult && m.tore_eigen > m.tore_gegner;
            const lost = hasResult && m.tore_eigen < m.tore_gegner;
            return (
              <div key={m.id}
                className="rounded-lg border border-zinc-800 bg-zinc-900/40 hover:border-brand-lighter/50 hover:bg-zinc-900 transition group"
                data-testid={`match-row-${m.id}`}>
                <button
                  onClick={() => navigate(`/noten/${m.id}`)}
                  className="w-full text-left p-4 flex flex-col sm:flex-row sm:items-center gap-3"
                >
                  <div className={"h-14 w-14 rounded-md flex items-center justify-center font-display font-bold tabular-nums shrink-0 " +
                    (m.spieltyp === "pflichtspiel" ? "bg-brand/15 text-brand-lighter" : "bg-amber-500/15 text-amber-300")}>
                    <div className="text-center leading-none">
                      <div className="text-[10px] uppercase tracking-wide opacity-75">{m.datum.slice(8,10)}.{m.datum.slice(5,7)}</div>
                      <div className="text-lg mt-0.5">{m.datum.slice(2,4)}</div>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-zinc-100 font-medium truncate">vs. {m.gegner}</div>
                    <div className="text-xs text-zinc-400 flex flex-wrap items-center gap-2">
                      <span>{formatDateDE(m.datum)}</span>
                      <span className={"px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide " + (m.spieltyp === "pflichtspiel" ? "text-brand-lighter bg-brand/10" : "text-amber-300 bg-amber-500/10")}>
                        {spieltypLabel(m.spieltyp)}
                      </span>
                      {hasResult && (
                        <span className={"font-semibold " + (won ? "text-emerald-400" : lost ? "text-rose-400" : "text-zinc-300")}>
                          {m.tore_eigen}:{m.tore_gegner}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="text-[10px] uppercase tracking-wide text-zinc-500">Ø Note</div>
                      <div className={"font-display text-2xl font-bold tabular-nums " + noteColor(avg)} data-testid={`match-avg-${m.id}`}>
                        {avg == null ? "—" : avg.toFixed(1)}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] uppercase tracking-wide text-zinc-500">Bewertet</div>
                      <div className="font-display text-lg font-bold text-zinc-300">{ratedCount}</div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-zinc-500 group-hover:text-brand-lighter shrink-0" />
                  </div>
                </button>
                <div className="border-t border-zinc-800 px-4 py-2 flex justify-end">
                  <button onClick={() => setConfirmDelete(m)} className="text-zinc-500 hover:text-red-400 text-xs flex items-center gap-1" data-testid={`match-delete-${m.id}`}>
                    <Trash2 className="h-3 w-3" /> Löschen
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-lg font-semibold tracking-tight">Spiel löschen?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              {confirmDelete && `${confirmDelete.gegner} (${formatDateDE(confirmDelete.datum)}) wird mit allen Bewertungen entfernt.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-zinc-100 hover:bg-zinc-700">Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600 text-white" data-testid="match-delete-confirm">Löschen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
